import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { WordCloudItem } from '@core/models/word-cloud-item.model';
import {
  layoutWordCloud,
  prepareWordCloudItems,
  selectVisibleWordCloudItems,
  toRelativePositions,
  type PreparedWordCloudItem,
  type WordCloudBox,
  type WordCloudPoint,
} from '@core/utils/word-cloud-layout.util';

const FONT_WEIGHT = 500;
const HIGHLIGHT_FONT_WEIGHT = 700;
const FALLBACK_CHARACTER_WIDTH = 0.55;
const FALLBACK_FONT_FAMILY = 'sans-serif';

@Component({
  selector: 'app-word-cloud-display',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './word-cloud-display.component.html',
  styleUrl: './word-cloud-display.component.css',
})
export class WordCloudDisplayComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() words: WordCloudItem[] = [];
  @Input() minFontSize = 12;
  @Input() maxFontSize = 48;
  @Input() maxWords = 50;
  @Input() ariaLabel = 'Word cloud';
  @Input() accentColor = '#b45309';

  @HostBinding('attr.role') readonly role = 'region';

  @HostBinding('attr.aria-label') get hostAriaLabel(): string {
    return this.ariaLabel;
  }

  prepared: PreparedWordCloudItem[] = [];
  boxes: WordCloudBox[] = [];
  viewBox = '0 0 0 0';

  private width = 0;
  private height = 0;
  private fontFamily = FALLBACK_FONT_FAMILY;
  private measureContext: CanvasRenderingContext2D | null = null;
  private previous = new Map<string, WordCloudPoint>();
  private resizeObserver?: ResizeObserver;
  private isDestroyed = false;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly changeDetector: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(): void {
    this.prepared = prepareWordCloudItems(this.words);
    this.layout();
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const element = this.host.nativeElement;
    const view = this.document.defaultView;
    this.measureContext = this.document.createElement('canvas').getContext('2d');
    this.fontFamily = view?.getComputedStyle(element).fontFamily || FALLBACK_FONT_FAMILY;
    this.updateSize();

    if (view?.ResizeObserver) {
      this.resizeObserver = new view.ResizeObserver(() => this.updateSize());
      this.resizeObserver.observe(element);
    }

    void this.document.fonts?.ready.then(() => {
      if (this.isDestroyed) {
        return;
      }
      this.fontFamily = view?.getComputedStyle(element).fontFamily || FALLBACK_FONT_FAMILY;
      this.layout();
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.resizeObserver?.disconnect();
  }

  trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  wordTransform(box: WordCloudBox): string {
    return `translate(${box.x.toFixed(2)}px, ${box.y.toFixed(2)}px)`;
  }

  wordWeight(box: WordCloudBox): number {
    return box.highlighted ? HIGHLIGHT_FONT_WEIGHT : FONT_WEIGHT;
  }

  wordFill(box: WordCloudBox): string {
    return box.highlighted ? this.accentColor : box.color;
  }

  private updateSize(): void {
    const element = this.host.nativeElement;
    const width = element.clientWidth;
    const height = element.clientHeight;
    if (width === this.width && height === this.height) {
      return;
    }
    this.width = width;
    this.height = height;
    this.viewBox = `0 0 ${width} ${height}`;
    this.layout();
    this.changeDetector.markForCheck();
  }

  private layout(): void {
    const items = selectVisibleWordCloudItems(this.prepared, this.maxWords);
    this.boxes = layoutWordCloud(items, {
      width: this.width,
      height: this.height,
      minFontSize: this.minFontSize,
      maxFontSize: this.maxFontSize,
      previous: this.previous,
      measureText: (text, fontSize) => this.measureText(text, fontSize),
    });

    if (this.width >= 1 && this.height >= 1) {
      this.previous = toRelativePositions(this.boxes, this.width, this.height);
    }
  }

  private measureText(text: string, fontSize: number): number {
    if (!this.measureContext) {
      return text.length * fontSize * FALLBACK_CHARACTER_WIDTH;
    }
    this.measureContext.font = `${HIGHLIGHT_FONT_WEIGHT} ${fontSize}px ${this.fontFamily}`;
    return this.measureContext.measureText(text).width;
  }
}
